import React from "react";
import { ShimmerBlock, StatCardSkeleton } from "../common/Skeleton";

export { StatCardSkeleton };

export function HolidaysCardSkeleton() {
  return (
    <div className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2 mb-[15px]">
        <ShimmerBlock className="w-[18px] h-[18px] rounded" />
        <ShimmerBlock className="h-4 w-40" />
      </div>
      <div className="flex flex-col gap-[3px]">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-[13px] py-[9px]">
            <ShimmerBlock className="w-[46px] h-[48px] rounded-[10px] flex-none" />
            <div className="flex-1 flex flex-col gap-1">
              <ShimmerBlock className="h-4 w-36" />
              <ShimmerBlock className="h-3 w-20" />
            </div>
            <ShimmerBlock className="h-[22px] w-[70px] rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

const EmployeeRowSkeleton = () => (
  <div className="flex items-center space-x-3 p-2 sm:p-3 rounded-lg">
    <div className="flex-shrink-0">
      <ShimmerBlock className="w-3 h-3 rounded-full" />
    </div>

    <div className="flex-shrink-0">
      <ShimmerBlock className="w-8 h-8 rounded-full" />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between">
        <ShimmerBlock className="h-4 w-3/4 max-w-[150px]" />
      </div>

      <div className="flex items-center space-x-2 mt-1">
        <ShimmerBlock className="h-3 w-16" />
        <ShimmerBlock className="h-4 w-20 rounded-full" />
      </div>
    </div>
  </div>
);

export function TeamStatusSkeleton() {
  const skeletonRows = Array.from({ length: 5 });

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-soft border border-slate-200 p-3 sm:p-4 md:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem] flex flex-col">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <ShimmerBlock className="h-6 w-32" />
      </div>

      <div className="mb-3 sm:mb-4">
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <ShimmerBlock className="w-4 h-4 rounded-full" />
              <ShimmerBlock className="w-14 h-3" />
            </div>
            <div className="flex items-center space-x-2">
              <ShimmerBlock className="w-4 h-4 rounded-full" />
              <ShimmerBlock className="w-14 h-3" />
            </div>
            <div className="flex items-center space-x-2">
              <ShimmerBlock className="w-4 h-4 rounded-full" />
              <ShimmerBlock className="w-14 h-3" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
        <div className="space-y-2 sm:space-y-3 pr-2">
          {skeletonRows.map((_, index) => (
            <EmployeeRowSkeleton key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

const JoinerRowSkeleton = () => (
  <div className="flex items-center space-x-3 p-2 rounded-lg">
    <div className="relative flex-shrink-0">
      <ShimmerBlock className="w-10 h-10 rounded-full" />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <ShimmerBlock className="h-4 w-40" />

          <ShimmerBlock className="h-3 w-28" />

          <div className="flex items-center space-x-2 mt-1">
            <ShimmerBlock className="w-3 h-3" />
            <ShimmerBlock className="h-3 w-32" />
          </div>

          <ShimmerBlock className="h-3 w-20" />
        </div>
      </div>
    </div>
  </div>
);

export function MonthlyJoinersSkeleton() {
  const skeletonRows = Array.from({ length: 3 });

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-soft border border-slate-200 p-3 sm:p-4 md:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem] flex flex-col">
      <div className="mb-4">
        <div className="flex items-center space-x-2">
          <ShimmerBlock className="w-6 h-6 rounded-full" />
          <ShimmerBlock className="h-6 w-36" />
          <ShimmerBlock className="h-5 w-10 rounded-full" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
        <div className="space-y-3">
          {skeletonRows.map((_, index) => (
            <JoinerRowSkeleton key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

const BirthdayAnniversaryRowSkeleton: React.FC = () => (
  <div className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded-lg transition-colors">
    <div className="flex-shrink-0">
      <ShimmerBlock className="w-10 h-10 rounded-full" />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <ShimmerBlock className="h-4 w-3/4 max-w-[180px] mb-1" />

          <ShimmerBlock className="h-3 w-20 mb-2" />

          <div className="flex items-center space-x-2 mt-1">
            <ShimmerBlock className="h-3 w-12" />
            <ShimmerBlock className="h-4 w-16 rounded-full" />
          </div>

          <ShimmerBlock className="h-3 w-24 mt-1" />
        </div>
      </div>
    </div>
  </div>
);

export const EmployeeInfoSkeleton: React.FC = () => {
  const detailRows = Array.from({ length: 6 });

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-soft border border-slate-200 p-4 sm:p-6 h-80 sm:h-[22rem] md:h-[24rem] 2xl:h-[28rem] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShimmerBlock className="w-5 h-5 rounded-full" />
          <ShimmerBlock className="h-6 w-40 rounded-md" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 pr-2">
        <div className="space-y-4">
          
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <ShimmerBlock className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
            </div>

            <div className="flex-1 min-w-0 space-y-2 pt-1">
              <ShimmerBlock className="h-6 w-3/4 rounded-md" />
              <ShimmerBlock className="h-4 w-1/2 rounded-md" />
              <ShimmerBlock className="h-4 w-20 rounded-md" />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <div className="flex flex-col space-y-3">
              {detailRows.map((_, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <ShimmerBlock className="h-4 w-24 rounded-md opacity-70" />
                  <ShimmerBlock className="h-4 w-32 rounded-md" />
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export function QuickStatsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-4 sm:p-5 flex items-center gap-3.5 shadow-[var(--shadow-sm)]"
        >
          <ShimmerBlock className="w-11 h-11 rounded-[13px] flex-none" />
          <div className="flex-1 space-y-2">
            <ShimmerBlock className="h-6 w-14" />
            <ShimmerBlock className="h-3 w-24" />
            <ShimmerBlock className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

export const BirthdayAnniversaryCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <ShimmerBlock className="w-[18px] h-[18px] rounded" />
          <ShimmerBlock className="h-4 w-28" />
        </div>
        <ShimmerBlock className="h-5 w-20 rounded-full" />
      </div>
      <div className="flex flex-col gap-2">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-layout-hover">
            <ShimmerBlock className="w-10 h-10 rounded-[12px] flex-none" />
            <div className="flex-1 flex flex-col gap-1.5">
              <ShimmerBlock className="h-4 w-28" />
              <ShimmerBlock className="h-3 w-20" />
            </div>
            <ShimmerBlock className="h-8 w-16 rounded-lg flex-none" />
          </div>
        ))}
      </div>
    </div>
  );
};
